import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Chip,
  Box,
  Typography,
  Alert,
  Autocomplete,
  Avatar,
} from '@mui/material';
import { useMessaging } from '../../contexts/MessagingContext';
import { useAuth } from '../../contexts/AuthContext';
import type { ConversationType } from '../../types/message';
import { MentionSearchService } from '../../services/mentionSearchService';
import type { MentionSearchResult } from '../../types/mention';

interface CreateConversationDialogProps {
  open: boolean;
  onClose: () => void;
}

/**
 * CreateConversationDialog Component
 *
 * Modal for creating new conversations with:
 * - Type selection (direct/group)
 * - Participant selection
 * - Group name (for groups)
 * - Group description (for groups)
 */
export const CreateConversationDialog: React.FC<CreateConversationDialogProps> = ({
  open,
  onClose,
}) => {
  const { authState } = useAuth();
  const user = authState.user;
  const { createConversation, setActiveConversation } = useMessaging();

  const [type, setType] = useState<ConversationType>('direct');
  const [participantInput, setParticipantInput] = useState('');
  const [participants, setParticipants] = useState<string[]>([]);
  const [participantDetails, setParticipantDetails] = useState<Map<string, MentionSearchResult>>(new Map());
  const [searchResults, setSearchResults] = useState<MentionSearchResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [groupName, setGroupName] = useState('');
  const [groupDescription, setGroupDescription] = useState('');
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Use useEffect to handle search with debouncing
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (participantInput.trim().length > 0) {
        handleSearchUsers(participantInput);
      } else {
        setSearchResults([]);
      }
    }, 300); // 300ms debounce

    return () => clearTimeout(timeoutId);
  }, [participantInput]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSearchUsers = async (searchTerm: string): Promise<void> => {
    if (!user || searchTerm.trim().length === 0) {
      setSearchResults([]);
      return;
    }

    setSearching(true);
    try {
      const results = await MentionSearchService.searchUsersForMention(
        searchTerm,
        { contentType: 'message', currentUserId: user.uid },
        20 // Get more results for better filtering
      );

      // Filter out self only (allow re-showing already selected users so they can be found)
      const filtered = results.filter(r => r.userId !== user.uid);
      setSearchResults(filtered);
    } catch (err) {
      console.error('Error searching users:', err);
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  };

  const handleAddParticipant = (selectedUser: MentionSearchResult | null): void => {
    if (!selectedUser || !user) return;

    // Validate userId exists
    if (!selectedUser.userId) {
      console.error('Selected user has no userId:', selectedUser);
      setError('Invalid user selected');
      return;
    }

    // Validate not adding self
    if (selectedUser.userId === user.uid) {
      setError('Cannot add yourself as a participant');
      return;
    }

    // Validate not duplicate
    if (participants.includes(selectedUser.userId)) {
      setError('Participant already added');
      return;
    }

    // For direct messages, enforce single recipient
    if (type === 'direct' && participants.length >= 1) {
      setError('Direct messages can only have one other participant');
      return;
    }

    // Add participant
    setParticipants([...participants, selectedUser.userId]);
    const newDetails = new Map(participantDetails);
    newDetails.set(selectedUser.userId, selectedUser);
    setParticipantDetails(newDetails);
    setParticipantInput('');
    setSearchResults([]);
    setError(null);
  };

  const handleRemoveParticipant = (userId: string): void => {
    setParticipants(participants.filter((id) => id !== userId));
  };

  const handleCreate = async (): Promise<void> => {
    if (!user) return;

    // Validation
    if (participants.length === 0) {
      setError('Please add at least one participant');
      return;
    }

    if (type === 'direct' && participants.length > 1) {
      setError('Direct messages can only have one other participant');
      return;
    }

    if (type === 'group' && participants.length < 2) {
      setError('Group chats must have at least 2 other participants (3 total including you)');
      return;
    }

    if (type === 'group' && !groupName.trim()) {
      setError('Please provide a group name');
      return;
    }

    setCreating(true);
    setError(null);

    try {
      // Add current user to participants
      const allParticipants = [user.uid, ...participants];

      // Build params object without undefined fields
      const params: any = {
        type,
        participants: allParticipants,
      };

      // Only add name/description for group chats if they have values
      if (type === 'group') {
        const trimmedName = groupName.trim();
        if (trimmedName) {
          params.name = trimmedName;
        }
        const trimmedDescription = groupDescription.trim();
        if (trimmedDescription) {
          params.description = trimmedDescription;
        }
      }

      const conversationId = await createConversation(params);

      // Set as active conversation
      setActiveConversation(conversationId);

      // Reset and close
      handleReset();
      onClose();
    } catch (err: any) {
      console.error('Error creating conversation:', err);
      setError(err.message || 'Failed to create conversation');
    } finally {
      setCreating(false);
    }
  };

  const handleReset = (): void => {
    setType('direct');
    setParticipantInput('');
    setParticipants([]);
    setParticipantDetails(new Map());
    setSearchResults([]);
    setGroupName('');
    setGroupDescription('');
    setError(null);
  };

  const handleClose = (): void => {
    handleReset();
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>New Conversation</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Type Selection */}
        <FormControl component="fieldset" sx={{ mb: 3 }}>
          <FormLabel component="legend">Conversation Type</FormLabel>
          <RadioGroup
            row
            value={type}
            onChange={(e) => setType(e.target.value as ConversationType)}
          >
            <FormControlLabel value="direct" control={<Radio />} label="Direct Message" />
            <FormControlLabel value="group" control={<Radio />} label="Group Chat" />
          </RadioGroup>
        </FormControl>

        {/* Group Name (if group) */}
        {type === 'group' && (
          <>
            <TextField
              fullWidth
              label="Group Name"
              value={groupName}
              onChange={(e) => setGroupName(e.target.value)}
              sx={{ mb: 2 }}
              required
            />
            <TextField
              fullWidth
              label="Group Description (optional)"
              value={groupDescription}
              onChange={(e) => setGroupDescription(e.target.value)}
              multiline
              rows={2}
              sx={{ mb: 2 }}
            />
          </>
        )}

        {/* Participant Selection */}
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          {type === 'direct' ? 'Select Recipient' : 'Add Participants'}
        </Typography>
        <Autocomplete
          fullWidth
          size="small"
          options={searchResults}
          getOptionLabel={(option) => option.displayName}
          renderOption={(props, option) => {
            // Extract key from props to avoid React warning
            const { key, ...otherProps } = props;
            return (
              <Box
                key={key}
                component="li"
                {...otherProps}
                sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
              >
                <Avatar
                  src={option.photoURL}
                  alt={option.displayName}
                  sx={{ width: 32, height: 32 }}
                >
                  {option.displayName.charAt(0).toUpperCase()}
                </Avatar>
                <Box>
                  <Typography variant="body2">{option.displayName}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {option.email}
                    {option.personName && ` • ${option.personName}`}
                  </Typography>
                </Box>
              </Box>
            );
          }}
          inputValue={participantInput}
          onInputChange={(event, newValue) => {
            setParticipantInput(newValue);
          }}
          onChange={(event, newValue) => {
            handleAddParticipant(newValue);
          }}
          loading={searching}
          loadingText="Searching users..."
          noOptionsText={participantInput ? 'No users found' : 'Type to search by name, username, or email'}
          disabled={creating || (type === 'direct' && participants.length >= 1)}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder={
                type === 'direct' && participants.length >= 1
                  ? 'Remove current recipient to select another'
                  : 'Search by name, username, or email...'
              }
            />
          )}
          sx={{ mb: 2 }}
        />

        {/* Selected Participants */}
        {participants.length > 0 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
            {participants.map((userId) => {
              const userDetails = participantDetails.get(userId);
              return (
                <Chip
                  key={userId}
                  avatar={
                    <Avatar src={userDetails?.photoURL} sx={{ width: 24, height: 24 }}>
                      {userDetails?.displayName?.charAt(0).toUpperCase() || 'U'}
                    </Avatar>
                  }
                  label={userDetails?.displayName || userId}
                  onDelete={() => handleRemoveParticipant(userId)}
                />
              );
            })}
          </Box>
        )}

        <Typography variant="caption" color="text.secondary">
          {type === 'direct'
            ? 'Add exactly one other participant for a direct message'
            : 'Add at least 2 other participants for a group chat (3+ total including you)'}
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={creating}>
          Cancel
        </Button>
        <Button onClick={handleCreate} variant="contained" disabled={creating}>
          {creating ? 'Creating...' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
